import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Module } from "../types/course.types";
import CustomTextField from "../../../../components/common/Textfield";
import { tokens } from "../../../../theme";

interface ModulesLessonsTabProps {
  modules: Module[];
  onModulesChange: (modules: Module[]) => void;
}

const ModulesLessonsTab: React.FC<ModulesLessonsTabProps> = ({
  modules,
  onModulesChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [newModuleDescription, setNewModuleDescription] = useState("");
  const [newLessonVideoUrl, setNewLessonVideoUrl] = useState("");
  const [newLessonDuration, setNewLessonDuration] = useState("");

  const handleAddModule = () => {
    if (!newModuleTitle.trim()) return;

    const newModule: Module = {
      id: `module-${Date.now()}`,
      title: newModuleTitle,
      description: newModuleDescription,
      order: modules.length,
      lessons: [],
    };

    onModulesChange([...modules, newModule]);
    setNewModuleTitle("");
    setNewModuleDescription("");
  };

  const handleDeleteModule = (moduleId: string) => {
    onModulesChange(modules.filter((m) => m.id !== moduleId));
  };

  const handleDeleteLesson = (moduleId: string, lessonId: string) => {
    const updatedModules = modules.map((m) => {
      if (m.id === moduleId) {
        return { ...m, lessons: m.lessons.filter((l) => l.id !== lessonId) };
      }
      return m;
    });
    onModulesChange(updatedModules);
  };

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Add Module Section */}
      <Paper sx={{ p: 2, backgroundColor: colors.primary[800], boxShadow: 0 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Add New Module
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              label="Module Title"
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              placeholder="e.g., Introduction to React"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              label="Module Description"
              value={newModuleDescription}
              onChange={(e) => setNewModuleDescription(e.target.value)}
              placeholder="Brief description"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddModule}
            >
              Add Module
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Modules List */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Modules ({modules.length})
        </Typography>

        {modules.length === 0 ? (
          <Paper sx={{ p: 2, textAlign: "center", color: "textSecondary" }}>
            <Typography>
              No modules added yet. Create one to get started!
            </Typography>
          </Paper>
        ) : (
          modules.map((module) => (
            <Paper
              key={module.id}
              sx={{ mb: 2, overflow: "hidden", boxShadow: 0 }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: colors.primary[800],
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#efefef" },
                }}
                onClick={() => toggleModuleExpansion(module.id)}
              >
                <ChevronRightIcon
                  sx={{
                    transform:
                      expandedModule === module.id ? "rotate(90deg)" : "none",
                    transition: "transform 0.3s",
                  }}
                />
                <Box sx={{ flex: 1, ml: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {module.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {module.description}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mr: 2 }}
                >
                  {module.lessons.length} lessons
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteModule(module.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Module Lessons */}
              <Collapse in={expandedModule === module.id}>
                <Box sx={{ p: 2, backgroundColor: colors.primary[900] }}>
                  {/* Add Lesson Section */}
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.primary[800],
                      boxShadow: 0,
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <CustomTextField
                          fullWidth
                          label="Video URL"
                          value={newLessonVideoUrl}
                          onChange={(e) => setNewLessonVideoUrl(e.target.value)}
                          placeholder="https://example.com/video.mp4"
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <CustomTextField
                          fullWidth
                          label="Duration (minutes)"
                          type="number"
                          value={newLessonDuration}
                          onChange={(e) => setNewLessonDuration(e.target.value)}
                          variant="outlined"
                          size="small"
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Lessons List */}
                  {module.lessons.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      No lessons yet
                    </Typography>
                  ) : (
                    <List>
                      {module.lessons.map((lesson) => (
                        <ListItem
                          key={lesson.id}
                          sx={{
                            border: `1px solid ${colors.primary[300]}`,
                            mb: 1,
                            borderRadius: 1,
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <ListItemText
                            primary={lesson.title}
                            secondary={`${lesson.duration} minutes`}
                          />
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              handleDeleteLesson(module.id, lesson.id)
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ModulesLessonsTab;
